import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { lazy, type ComponentType } from 'react';
import { MainLayout, AuthLayout } from '@/components/layout';
import { ProtectedRoute, GuestRoute, SuperAdminRoute, TenantRoute, ProviderAdminRoute, ProviderCapabilityRoute, AdminOrTenantRoute } from '@/components/RouteGuards';
import { ModuleRoute } from '@/components/ModuleRoute';
import { ProviderModuleRoute } from '@/components/ProviderModuleRoute';
import { ModuleKeys } from '@/types';
import { LoginPage, VerifyOtpPage, AdminLoginPage, ForgotPasswordPage, ResetPasswordPage, AcceptInvitePage, BusinessSignupPage, VerifyEmailPage } from '@/features/auth';

function lazyNamed<TModule, TKey extends keyof TModule>(
  loader: () => Promise<TModule>,
  exportName: TKey,
) {
  return lazy(async () => ({
    default: (await loader())[exportName] as ComponentType,
  }));
}

const DashboardPage = lazyNamed(() => import('@/features/dashboard'), 'DashboardPage');
const OrdersPage = lazyNamed(() => import('@/features/orders'), 'OrdersPage');
const OrderDetailsPage = lazyNamed(() => import('@/features/orders'), 'OrderDetailsPage');
const CommentsMonitorPage = lazyNamed(() => import('@/features/orders'), 'CommentsMonitorPage');
const CustomersPage = lazyNamed(() => import('@/features/customers'), 'CustomersPage');
const CustomerDetailsPage = lazyNamed(() => import('@/features/customers'), 'CustomerDetailsPage');
const ProvidersPage = lazyNamed(() => import('@/features/providers'), 'ProvidersPage');
const ProviderDetailsPage = lazyNamed(() => import('@/features/providers'), 'ProviderDetailsPage');
const ProviderDebtsPage = lazyNamed(() => import('@/features/providers'), 'ProviderDebtsPage');
const ProviderRegistrationPage = lazyNamed(() => import('@/features/providers'), 'ProviderRegistrationPage');
const ProviderStatusPage = lazyNamed(() => import('@/features/providers'), 'ProviderStatusPage');
const ProviderApprovalPage = lazyNamed(() => import('@/features/providers'), 'ProviderApprovalPage');
const TicketsPage = lazyNamed(() => import('@/features/tickets'), 'TicketsPage');
const TicketDetailsPage = lazyNamed(() => import('@/features/tickets'), 'TicketDetailsPage');
const CreateTicketPage = lazyNamed(() => import('@/features/tickets'), 'CreateTicketPage');
const CouponsPage = lazyNamed(() => import('@/features/coupons'), 'CouponsPage');
const CouponDetailsPage = lazyNamed(() => import('@/features/coupons'), 'CouponDetailsPage');
const OffersPage = lazyNamed(() => import('@/features/offers'), 'OffersPage');
const OfferDetailsPage = lazyNamed(() => import('@/features/offers'), 'OfferDetailsPage');
const FaqsPage = lazyNamed(() => import('@/features/faqs'), 'FaqsPage');
const ReviewsPage = lazyNamed(() => import('@/features/reviews'), 'ReviewsPage');
const NotificationsPage = lazyNamed(() => import('@/features/notifications'), 'NotificationsPage');
const SettingsPage = lazyNamed(() => import('@/features/settings'), 'SettingsPage');
const LookupsPage = lazyNamed(() => import('@/features/settings'), 'LookupsPage');
const DynamicConfigPage = lazyNamed(() => import('@/features/settings'), 'DynamicConfigPage');
const CategoriesPage = lazyNamed(() => import('@/features/settings'), 'CategoriesPage');
const CitiesPage = lazyNamed(() => import('@/features/settings'), 'CitiesPage');
const NationalitiesPage = lazyNamed(() => import('@/features/settings'), 'NationalitiesPage');
const AppVersionsPage = lazyNamed(() => import('@/features/app-versions'), 'AppVersionsPage');
const SpecialtyDetailsPage = lazyNamed(() => import('@/features/specialties'), 'SpecialtyDetailsPage');
const SpecialtyFormPage = lazyNamed(() => import('@/features/specialties'), 'SpecialtyFormPage');
const CategoryDetailsPage = lazyNamed(() => import('@/features/catalog'), 'CategoryDetailsPage');
const SubCategoriesPage = lazyNamed(() => import('@/features/catalog'), 'SubCategoriesPage');
const SubSpecialtiesPage = lazyNamed(() => import('@/features/catalog'), 'SubSpecialtiesPage');
const DispatchersPage = lazyNamed(() => import('@/features/catalog'), 'DispatchersPage');
const MaksabServicesPage = lazyNamed(() => import('@/features/catalog'), 'MaksabServicesPage');
const ProductsPage = lazyNamed(() => import('@/features/products'), 'ProductsPage');
const SearchPage = lazyNamed(() => import('@/features/search'), 'SearchPage');
const OrdersMapPage = lazyNamed(() => import('@/features/maps'), 'OrdersMapPage');
const ProvidersMapPage = lazyNamed(() => import('@/features/maps'), 'ProvidersMapPage');
const NearbyMapPage = lazyNamed(() => import('@/features/maps'), 'NearbyMapPage');
const TransactionsPage = lazyNamed(() => import('@/features/transactions'), 'TransactionsPage');
const TenantsPage = lazyNamed(() => import('@/features/tenants'), 'TenantsPage');
const TenantDetailsPage = lazyNamed(() => import('@/features/tenants'), 'TenantDetailsPage');
const TenantCreatePage = lazyNamed(() => import('@/features/tenants'), 'TenantCreatePage');
const BrandingPage = lazyNamed(() => import('@/features/branding'), 'BrandingPage');
const SlidersPage = lazyNamed(() => import('@/features/branding'), 'SlidersPage');
const SupportSettingsPage = lazyNamed(() => import('@/features/support-hours'), 'SupportSettingsPage');
const SubscriptionsPage = lazyNamed(() => import('@/features/subscriptions/SubscriptionsPage'), 'SubscriptionsPage');
const SubscriptionDetailsPage = lazyNamed(() => import('@/features/subscriptions/SubscriptionDetailsPage'), 'SubscriptionDetailsPage');
const TenantAdminsPage = lazyNamed(() => import('@/features/platform'), 'TenantAdminsPage');
const PlatformAnalyticsPage = lazyNamed(() => import('@/features/platform'), 'PlatformAnalyticsPage');
const PlatformSettingsPage = lazyNamed(() => import('@/features/platform'), 'PlatformSettingsPage');
const PlatformFirebasePage = lazyNamed(() => import('@/features/platform'), 'PlatformFirebasePage');
const NotificationSettingsPage = lazy(() => import('@/features/notification-settings/NotificationSettingsPage'));
const RecurringJobsPage = lazyNamed(() => import('@/features/recurring-jobs'), 'RecurringJobsPage');
const NotificationLogsPage = lazyNamed(() => import('@/features/notification-logs'), 'NotificationLogsPage');
const UsersSecurityPage = lazyNamed(() => import('@/features/security'), 'UsersSecurityPage');
const PublicTrackingPage = lazyNamed(() => import('@/features/public'), 'PublicTrackingPage');

const ProviderDashboardPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderDashboardPage');
const ProviderOrdersPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderOrdersPage');
const ProviderShopOrdersPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderShopOrdersPage');
const ProviderTeamPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderTeamPage');
const ProviderTeamMemberPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderTeamMemberPage');
const ProviderCalendarPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderCalendarPage');
const ProviderServicesPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderServicesPage');
const ProviderProductsPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderProductsPage');
const ProviderVariantsPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderVariantsPage');
const ProviderZonesPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderZonesPage');
const ProviderFinancePage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderFinancePage');
const ProviderCustomersPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderCustomersPage');
const ProviderSettingsPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderSettingsPage');
const ProviderReportsPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderReportsPage');
const ProviderVehiclesPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderVehiclesPage');
const ProviderWalletsPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderWalletsPage');
const ProviderOperationsPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderOperationsPage');
const ProviderReturnsPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderReturnsPage');
const ProviderScorecardsPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderScorecardsPage');
const ProviderCashReconciliationPage = lazyNamed(() => import('@/features/providerAdmin'), 'ProviderCashReconciliationPage');

export const router = createBrowserRouter([
  // Auth routes
  {
    path: '/',
    element: (
      <GuestRoute>
        <AuthLayout />
      </GuestRoute>
    ),
    children: [
      { path: 'login', element: <LoginPage /> },
      { path: 'admin-login', element: <AdminLoginPage /> },
      { path: 'admin-signup', element: <BusinessSignupPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'verify-otp', element: <VerifyOtpPage /> },
      { path: 'accept-invite', element: <AcceptInvitePage /> },
    ],
  },

  // Phase 3.Z — verify-email lives outside GuestRoute so a freshly-signed-up
  // owner who's already authenticated can land here from the verify URL
  // without being redirected to /. Renders standalone (no AuthLayout).
  { path: '/verify-email', element: <VerifyEmailPage /> },

  // BZ5 (2026-05-24) — public order tracking page. No auth: the URL's code IS
  // the auth. Lives outside the protected/guest layouts so customers (who
  // are NOT logged in to the dashboard) can open the link.
  { path: '/t/:code', element: <PublicTrackingPage /> },

  // Protected routes
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      // Dashboard (redirects superadmin to /tenants if not impersonating)
      { index: true, element: <DashboardPage /> },

      // Search
      { path: 'search', element: <TenantRoute><SearchPage /></TenantRoute> },

      // Orders
      { path: 'orders', element: <TenantRoute><OrdersPage /></TenantRoute> },
      { path: 'orders/comments', element: <TenantRoute><CommentsMonitorPage /></TenantRoute> },
      { path: 'orders/:id', element: <TenantRoute><OrderDetailsPage /></TenantRoute> },

      // Customers
      { path: 'customers', element: <TenantRoute><CustomersPage /></TenantRoute> },
      { path: 'customers/:id', element: <TenantRoute><CustomerDetailsPage /></TenantRoute> },

      // Providers
      { path: 'providers', element: <TenantRoute><ProvidersPage /></TenantRoute> },
      { path: 'providers/register', element: <TenantRoute><ProviderRegistrationPage /></TenantRoute> },
      { path: 'providers/debts', element: <TenantRoute><ProviderDebtsPage /></TenantRoute> },
      { path: 'providers/approval', element: <TenantRoute><ProviderApprovalPage /></TenantRoute> },
      { path: 'providers/status', element: <TenantRoute><ProviderStatusPage /></TenantRoute> },
      { path: 'providers/:id', element: <TenantRoute><ProviderDetailsPage /></TenantRoute> },

      // Transactions
      { path: 'transactions', element: <TenantRoute><TransactionsPage /></TenantRoute> },

      // Specialties — the standalone list is folded into the category
      // drill-down (/categories/:id). Detail/form routes stay; the old
      // list path now redirects to categories.
      { path: 'specialties', element: <Navigate to="/categories" replace /> },
      { path: 'specialties/new', element: <TenantRoute><SpecialtyFormPage /></TenantRoute> },
      { path: 'specialties/:id', element: <TenantRoute><SpecialtyDetailsPage /></TenantRoute> },
      { path: 'specialties/:id/edit', element: <TenantRoute><SpecialtyFormPage /></TenantRoute> },

      // Dispatchers (specialty dispatcher configuration)
      { path: 'dispatchers', element: <TenantRoute><DispatchersPage /></TenantRoute> },

      // Catalog Management
      { path: 'sub-categories', element: <TenantRoute><SubCategoriesPage /></TenantRoute> },
      { path: 'sub-specialties', element: <TenantRoute><SubSpecialtiesPage /></TenantRoute> },
      { path: 'services', element: <TenantRoute><MaksabServicesPage /></TenantRoute> },

      // Products (Shop module)
      { path: 'products', element: <TenantRoute><ModuleRoute module={ModuleKeys.Shop}><ProductsPage /></ModuleRoute></TenantRoute> },

      // Tickets
      { path: 'tickets', element: <TenantRoute><TicketsPage /></TenantRoute> },
      { path: 'tickets/create', element: <TenantRoute><CreateTicketPage /></TenantRoute> },
      { path: 'tickets/:id', element: <TenantRoute><TicketDetailsPage /></TenantRoute> },

      // Coupons
      { path: 'coupons', element: <TenantRoute><CouponsPage /></TenantRoute> },
      { path: 'coupons/:code', element: <TenantRoute><CouponDetailsPage /></TenantRoute> },

      // Offers
      { path: 'offers', element: <TenantRoute><OffersPage /></TenantRoute> },
      { path: 'offers/:id', element: <TenantRoute><OfferDetailsPage /></TenantRoute> },

      // Reviews
      { path: 'reviews', element: <TenantRoute><ReviewsPage /></TenantRoute> },

      // FAQs
      { path: 'faqs', element: <TenantRoute><FaqsPage /></TenantRoute> },

      // Notifications
      { path: 'notifications', element: <TenantRoute><NotificationsPage /></TenantRoute> },

      // Data Entry (top-level routes)
      { path: 'categories', element: <TenantRoute><CategoriesPage /></TenantRoute> },
      { path: 'categories/:id', element: <TenantRoute><CategoryDetailsPage /></TenantRoute> },
      // City/District/Nationality are GLOBAL platform data (no
      // ITenantEntity — Features/Platform/Lookups). A tenant admin
      // editing them corrupts every tenant, so these CRUD pages are
      // SuperAdmin-only (was TenantRoute = data-integrity risk).
      { path: 'cities', element: <SuperAdminRoute><CitiesPage /></SuperAdminRoute> },
      { path: 'nationalities', element: <SuperAdminRoute><NationalitiesPage /></SuperAdminRoute> },
      { path: 'config', element: <TenantRoute><DynamicConfigPage /></TenantRoute> },
      { path: 'lookups', element: <TenantRoute><LookupsPage /></TenantRoute> },

      // Settings
      { path: 'settings', element: <TenantRoute><SettingsPage /></TenantRoute> },
      { path: 'profile', element: <TenantRoute><SettingsPage /></TenantRoute> },

      // Support Hours
      { path: 'support-hours', element: <TenantRoute><SupportSettingsPage /></TenantRoute> },

      // Recurring Jobs (TenantAdmin)
      { path: 'recurring-jobs', element: <TenantRoute><RecurringJobsPage /></TenantRoute> },

      // Notification Logs (TenantAdmin)
      { path: 'notification-logs', element: <TenantRoute><NotificationLogsPage /></TenantRoute> },

      // Users Security (TenantAdmin)
      { path: 'users-security', element: <TenantRoute><UsersSecurityPage /></TenantRoute> },

      // Provider Admin routes
      {
        path: 'business',
        element: <ProviderAdminRoute><ProviderCapabilityRoute><Outlet /></ProviderCapabilityRoute></ProviderAdminRoute>,
        children: [
          { index: true, element: <ProviderDashboardPage /> },
          { path: 'orders', element: <ProviderOrdersPage /> },
          { path: 'shop-orders', element: <ProviderModuleRoute anyOf={[ModuleKeys.Shop]}><ProviderShopOrdersPage /></ProviderModuleRoute> },
          { path: 'team', element: <ProviderTeamPage /> },
          { path: 'team/:id', element: <ProviderTeamMemberPage /> },
          { path: 'calendar', element: <ProviderModuleRoute anyOf={[ModuleKeys.Booking]}><ProviderCalendarPage /></ProviderModuleRoute> },
          { path: 'services', element: <ProviderModuleRoute anyOf={[ModuleKeys.HomeServices, ModuleKeys.Booking, ModuleKeys.OnlineServices]}><ProviderServicesPage /></ProviderModuleRoute> },
          { path: 'products', element: <ProviderModuleRoute anyOf={[ModuleKeys.Shop]}><ProviderProductsPage /></ProviderModuleRoute> },
          { path: 'products/:id/variants', element: <ProviderModuleRoute anyOf={[ModuleKeys.Shop]}><ProviderVariantsPage /></ProviderModuleRoute> },
          // Unified dispatch workbench (BZ3, 2026-05-23). Replaces the
          // three separate pages /business/{dispatch,fleet,board} with one
          // page that has a List/Kanban/Map view toggle. The three old
          // routes redirect here for a 30-day window then get removed.
          { path: 'operations', element: <ProviderOperationsPage /> },
          { path: 'dispatch', element: <Navigate to="/business/operations?view=list" replace /> },
          { path: 'fleet', element: <Navigate to="/business/operations?view=map" replace /> },
          { path: 'board', element: <Navigate to="/business/operations?view=kanban" replace /> },
          { path: 'zones', element: <ProviderZonesPage /> },
          { path: 'vehicles', element: <ProviderVehiclesPage /> },
          { path: 'wallets', element: <ProviderWalletsPage /> },
          { path: 'returns', element: <ProviderModuleRoute anyOf={[ModuleKeys.Shop]}><ProviderReturnsPage /></ProviderModuleRoute> },
          { path: 'reports', element: <ProviderReportsPage /> },
          { path: 'scorecards', element: <ProviderScorecardsPage /> },
          { path: 'cash-reconciliation', element: <ProviderCashReconciliationPage /> },
          { path: 'finance', element: <ProviderFinancePage /> },
          { path: 'customers', element: <ProviderCustomersPage /> },
          { path: 'settings', element: <ProviderSettingsPage /> },
        ],
      },

      // Legacy routes (redirect to new paths)
      { path: 'settings/app-versions', element: <Navigate to="/app-versions" replace /> },
      { path: 'settings/categories', element: <Navigate to="/categories" replace /> },
      { path: 'settings/cities', element: <Navigate to="/cities" replace /> },
      { path: 'settings/nationalities', element: <Navigate to="/nationalities" replace /> },
      { path: 'settings/config', element: <Navigate to="/config" replace /> },
      { path: 'settings/lookups', element: <Navigate to="/lookups" replace /> },

      // Maps
      { path: 'maps/orders', element: <TenantRoute><OrdersMapPage /></TenantRoute> },
      { path: 'maps/providers', element: <TenantRoute><ProvidersMapPage /></TenantRoute> },
      { path: 'maps/nearby', element: <TenantRoute><NearbyMapPage /></TenantRoute> },

      // Branding
      { path: 'branding', element: <TenantRoute><BrandingPage /></TenantRoute> },
      { path: 'branding/sliders', element: <TenantRoute><SlidersPage /></TenantRoute> },

      // SuperAdmin-only: Tenants
      {
        path: 'tenants',
        element: (
          <SuperAdminRoute>
            <TenantsPage />
          </SuperAdminRoute>
        ),
      },
      {
        path: 'tenants/create',
        element: (
          <SuperAdminRoute>
            <TenantCreatePage />
          </SuperAdminRoute>
        ),
      },
      {
        path: 'tenants/:id',
        element: (
          <SuperAdminRoute>
            <TenantDetailsPage />
          </SuperAdminRoute>
        ),
      },

      // SuperAdmin-only: Subscriptions
      {
        path: 'subscriptions',
        element: (
          <SuperAdminRoute>
            <SubscriptionsPage />
          </SuperAdminRoute>
        ),
      },
      {
        path: 'subscriptions/:tenantId',
        element: (
          <SuperAdminRoute>
            <SubscriptionDetailsPage />
          </SuperAdminRoute>
        ),
      },

      // SuperAdmin-only: Tenant Admins Management
      {
        path: 'tenant-admins',
        element: (
          <SuperAdminRoute>
            <TenantAdminsPage />
          </SuperAdminRoute>
        ),
      },

      // SuperAdmin-only: Platform Analytics
      {
        path: 'analytics',
        element: (
          <SuperAdminRoute>
            <PlatformAnalyticsPage />
          </SuperAdminRoute>
        ),
      },

      // SuperAdmin-only: Platform Settings
      {
        path: 'platform-settings',
        element: (
          <SuperAdminRoute>
            <PlatformSettingsPage />
          </SuperAdminRoute>
        ),
      },

      // SuperAdmin-only: Platform-wide Firebase admin
      // (per-tenant Firebase ops live at /tenants/{id}?tab=firebase)
      {
        path: 'platform/firebase',
        element: (
          <SuperAdminRoute>
            <PlatformFirebasePage />
          </SuperAdminRoute>
        ),
      },

      // Notification Settings — documented as SuperAdmin + TenantAdmin
      // access, but was rendered UNGUARDED (any authenticated user,
      // incl. provideradmin, could edit global notification defaults).
      // TenantRoute matches the intent and (post-B1) excludes
      // provideradmin while still admitting tenantadmin/support and an
      // impersonating SuperAdmin.
      { path: 'notification-settings', element: <AdminOrTenantRoute><NotificationSettingsPage /></AdminOrTenantRoute> },

      // App Versions — AppVersion is tenant-scoped (ITenantEntity), so
      // TenantRoute is correct; it was rendered UNGUARDED (reachable by
      // provideradmin / any role). TenantRoute closes that hole and
      // matches the documented SuperAdmin+TenantAdmin intent.
      { path: 'app-versions', element: <TenantRoute><AppVersionsPage /></TenantRoute> },
    ],
  },

  // 404
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
